import type { FC } from 'react';
import { useCallback } from 'react';
import { API } from '../../api';
import { Button } from '../../components/button';
import { message } from '../../components/message';
import { cx } from '../../cx';
import { useOnceAnyDay } from '../../hooks/use-once-any-day';
import { Icon } from '../../icon-svg';
import { fetchAiSpaceStatus, useAiSpaceStatus } from '../hooks/ai-limit-store';
import { useConversationContext } from '../hooks/context';
import { useChatTranslation } from '../hooks/use-i18n';

export const ApplySpaceAiBar: FC<{
  className?: string;
  isCol?: boolean;
  provider?: string;
}> = ({ className, isCol = false, provider }) => {
  const { spaceId } = useConversationContext();
  const once = useOnceAnyDay(`applySpaceAiBar-${spaceId}-${provider}`, {
    endDay: -1,
  });

  const status = useAiSpaceStatus(spaceId);
  const { t } = useChatTranslation();

  if (!once.showOnce) {
    return null;
  }

  if (status === 'ready') {
    return null;
  }

  return (
    <div
      className={cx(
        'flex items-center justify-between py-2.5 px-4 bg-ai_color/10 rounded',
        isCol && 'items-start'
      )}
    >
      <div
        className={cx('flex items-center space-x-2', isCol && 'flex flex-col space-y-2', className)}
      >
        <div className="bg-gradient-to-r from-[#FF3BFF] via-[#5C24FF] to-[#D94FD5] text-transparent bg-clip-text">
          {t('applyAiSearch')}
        </div>
        <ApplySpaceAiButton />
      </div>
      <Icon className="text-grey3" name="IcClose" size="xsmall" onClick={once.close} />
    </div>
  );
};

export const ApplySpaceAiButton: FC<{ className?: string }> = ({ className }) => {
  const { t } = useChatTranslation();
  const { spaceId } = useConversationContext();
  const status = useAiSpaceStatus(spaceId);

  const handleApply = useCallback(async () => {
    await API.ai.applyQa(spaceId);
    message.success(t('applySuccess'));
    void fetchAiSpaceStatus(spaceId);
  }, [spaceId, t]);

  if (status === 'none') {
    return (
      <Button colorType="primary" className={cx('h-fit text-t4', className)} onClick={handleApply}>
        {t('applyButton')}
      </Button>
    );
  }

  return (
    <div className={cx('flex items-center space-x-1 text-grey3', className)}>
      <Icon size="small" name="IcHistory" />
      <span>{t('queuePosition', { position: status })}</span>
    </div>
  );
};
